import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "./CartContext";
import { inr } from "@/data/products";
import ZipCTA from "./ZipCTA";

const DENIM = "url('/img/denim/denim-texture.jpg')";

function Fab() {
    const { count, setOpen, open, bump } = useCart();
    return (
        <button
            data-testid="pocket-cart-fab"
            onClick={() => setOpen(!open)}
            aria-label="Open your pocket"
            style={{ backgroundImage: DENIM }}
            className="denim-bg group fixed bottom-5 left-5 z-[65] h-16 w-14 overflow-hidden rounded-t-md rounded-b-[26px] border-2 border-gold/50 shadow-2xl shadow-black/60"
        >
            <div className="absolute inset-0 bg-obsidian/35 transition-colors group-hover:bg-obsidian/10" />
            <div className="absolute inset-1 rounded-b-[20px] border border-dashed border-gold/60" />
            <span className="rivet absolute left-1 top-1 h-2.5 w-2.5 rounded-full" aria-hidden="true" />
            <span className="rivet absolute right-1 top-1 h-2.5 w-2.5 rounded-full" aria-hidden="true" />
            {count > 0 && (
                <motion.span
                    key={bump}
                    initial={{ scale: 1.6 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    data-testid="pocket-cart-count"
                    className="absolute -right-1.5 -top-1.5 z-10 grid h-6 w-6 place-items-center rounded-full bg-gold font-mono text-[11px] font-bold text-black"
                >
                    {count}
                </motion.span>
            )}
        </button>
    );
}

function Drawer() {
    const { items, open, setOpen, remove, subtotal, count } = useCart();
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        className="fixed inset-0 z-[91] bg-black/60 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setOpen(false)}
                    />
                    <motion.aside
                        data-testid="pocket-cart"
                        style={{ backgroundImage: DENIM }}
                        className="denim-bg fixed right-0 top-0 z-[92] h-full w-[400px] max-w-[92vw] border-l border-white/10"
                        initial={{ x: "106%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "106%" }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <div className="absolute inset-0 bg-obsidian/70" />
                        <div className="pointer-events-none absolute inset-3 rounded-b-[44px] border-2 border-dashed border-gold/50" />
                        <div className="relative z-10 flex h-full flex-col p-7">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold">The Pocket</p>
                                    <h3 className="mt-1 font-display text-xl font-bold uppercase text-ink">
                                        Your Bag {count > 0 && `(${count})`}
                                    </h3>
                                </div>
                                <button
                                    data-testid="pocket-cart-close"
                                    onClick={() => setOpen(false)}
                                    aria-label="Close bag"
                                    className="rivet grid h-10 w-10 place-items-center rounded-full"
                                >
                                    <X size={15} className="relative z-10 text-[#3c1e06]" />
                                </button>
                            </div>

                            {items.length === 0 ? (
                                <div className="flex flex-1 flex-col items-center justify-center text-center" data-testid="pocket-cart-empty">
                                    <p className="font-display text-lg font-bold uppercase text-ink">The pocket is empty.</p>
                                    <p className="mt-2 max-w-[220px] text-sm leading-relaxed text-mute">
                                        Add a piece from the collection and it will drop right in here.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="mt-6 flex-1 space-y-3 overflow-y-auto pr-1">
                                        <AnimatePresence initial={false}>
                                            {items.map((it) => (
                                                <motion.div
                                                    key={it.key}
                                                    initial={{ y: -36, opacity: 0 }}
                                                    animate={{ y: 0, opacity: 1 }}
                                                    exit={{ x: 60, opacity: 0 }}
                                                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                                    className="flex items-center gap-4 border border-white/10 bg-black/40 p-3 backdrop-blur"
                                                    data-testid={`cart-item-${it.productId}`}
                                                >
                                                    <img src={it.img} alt={it.name} className="h-16 w-14 shrink-0 object-cover" />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="truncate font-display text-sm font-semibold text-ink">{it.name}</p>
                                                        <p className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.2em] text-mute">
                                                            Size {it.size} · Qty {it.qty}
                                                        </p>
                                                        <p className="mt-1 font-mono text-sm text-gold">{inr(it.price * it.qty)}</p>
                                                    </div>
                                                    <button
                                                        data-testid={`cart-remove-${it.productId}`}
                                                        onClick={() => remove(it.key)}
                                                        aria-label={`Remove ${it.name}`}
                                                        className="grid h-8 w-8 shrink-0 place-items-center border border-white/15 text-mute transition-colors hover:border-gold hover:text-gold"
                                                    >
                                                        <X size={13} />
                                                    </button>
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </div>
                                    <div className="border-t border-dashed border-gold/40 pt-5">
                                        <div className="flex items-center justify-between">
                                            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-mute">Subtotal</p>
                                            <p className="font-display text-xl font-bold text-gold" data-testid="pocket-cart-subtotal">
                                                {inr(subtotal)}
                                            </p>
                                        </div>
                                        <ZipCTA
                                            type="button"
                                            testid="pocket-cart-checkout"
                                            className="mt-5 w-full"
                                            onClick={() => toast.success("Checkout opens on the full URWAN store — this pocket is a preview.")}
                                        >
                                            Checkout
                                        </ZipCTA>
                                        <p className="mt-3 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-mute">
                                            Free shipping · Lifetime stitch &amp; tear warranty
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}

export default function PocketCart() {
    return (
        <>
            <Fab />
            <Drawer />
        </>
    );
}
